import { UseMutateFunction, useMutation } from '@tanstack/react-query';
import { Col, Form, Row } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import { GET_ALL_PLANT } from 'apis/GraphQL/Queries/plantQuery';
import { PlantApi } from 'apis/PlantApis';
import { SaleTeamApi } from 'apis/SaleTeamApi';
import { AxiosResponse } from 'axios';
import { FormFilter, SelectForm } from 'components';
import LazySelectForm, {
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { useUser } from 'contexts/UserProvider';
import useQueryParam from 'hooks/useQueryPrams';
import { IPlant } from 'interfaces/Plant/Plant';
import {
  IFilterTeamSale,
  IFilterTeamSaleCb,
  IGetUserSaleInCombobox,
  IParams,
  Item,
  STATUS_TS,
} from 'interfaces/TeamSale/TeamSale';
import { IUser } from 'interfaces/User/User';
import Content from 'layouts/Content';
import {
  STATUS_TS_OPTIONS,
  TEAM_TYPE_OPTIONS,
} from 'utils/constants/TeamSale/TeamSale';

const spanCol = 8;

const paramsGetPlants: IParams = {
  query: GET_ALL_PLANT,
  variables: {
    skip: 0,
    take: 999,
    input: {
      id: {
        not: '5000',
      },
    },
  },
};

interface IFilterSaleTeamPage {
  isOpen: boolean;
  defaultFilter?: IFilterTeamSale;
  saleTeamMutate: {
    isPending: boolean;
    mutate: UseMutateFunction<
      AxiosResponse<any, any>,
      Error,
      IFilterTeamSaleCb,
      unknown
    >;
    data: Item[];
    totalItems: number;
  };
  onFilter(values: any): void;
  onCancel: () => void;
}

const FilterSaleTeamPage = forwardRef(
  (
    { isOpen, defaultFilter, saleTeamMutate, onFilter }: IFilterSaleTeamPage,
    ref,
  ) => {
    const [form] = Form.useForm();
    const { user } = useUser();

    const queryParam = useQueryParam(),
      page = parseInt(queryParam.get('page') + '') || 1;

    const [dataPlants, setDataPlants] = useState<IPlant[]>([]),
      [teamData, setTeamData] = useState(initLazySelectData),
      [userData, setUserData] = useState<{
        options: DefaultOptionType[];
        totalItems: number;
      }>({ options: [] as IUser[], totalItems: 0 });

    const OPTIONS_PLANT: DefaultOptionType[] = [...dataPlants].map((plant) => {
      return {
        value: plant.id,
        label: plant.id === '' ? plant.name : plant.id,
      };
    });

    const { mutate: getPlants } = useMutation({
        mutationFn: () => PlantApi.getAllPlant(),
        onSuccess: (res) => {
          if (res?.data?.getPlant) {
            setDataPlants(res?.data?.getPlant?.items);
          }
        },
        onError: (error) => {
          console.log(error);
        },
      }),
      {
        isPending: isGetUserRoleSaleInCBBPending,
        mutate: getUserRoleSaleInCBB,
      } = useMutation({
        mutationFn: (params: IGetUserSaleInCombobox) =>
          SaleTeamApi.getUserRoleSaleInCBB(params),
        onSuccess: (res) => {
          if (res?.data?.getUser)
            setUserData({
              options:
                res.data?.getUser?.items.map((item: any) => ({
                  label: `${item?.id} - ${item?.fullName}`,
                  value: item?.id,
                })) ?? ([] as IUser[]),
              totalItems: res.data?.getUser.pageInfo.totalItems ?? 0,
            });
        },
        onError: (error) => {
          console.log(error);
        },
      });

    const onFinish = (values: any) => {
      const [, teamSubId, plantId] = (values.teamSaleId || '')?.split('.');

      onFilter({
        ...values,
        teamSaleId: teamSubId || values.teamSaleId,
        plantId: plantId || values.plantId,
        page: 1,
      });
    };

    const handleCancel = () => {
      form.resetFields();
      form.setFieldsValue(defaultFilter);
      const values = form.getFieldsValue();

      onFilter({
        ...values,
        teamSalesId: values.teamSalesId?.split('.')[1] || values.teamSalesId,
      });

      // handleTeamSelectScrollChange({ pageSize: 10, value: '' });
      // handleUserSelectScrollChange({ pageSize: 10, value: '' });
    };

    useImperativeHandle(ref, () => ({ handleCancel }));

    useEffect(() => {
      if (user?.isAdmin || user?.isHO) getPlants();

      form.setFieldsValue({
        ...form.getFieldsValue(),
        ...defaultFilter,
      });

      onFilter({
        statusId: STATUS_TS.active,
        plantId: user?.isAdmin || user?.isHO ? undefined : user?.plantCode,
        userId: user?.isSale ? user?.id : undefined,
        page: page,
      });
    }, [user, defaultFilter]);

    useEffect(() => {
      setTeamData({
        options: saleTeamMutate.data.map((item: any) => ({
          value: `${item?.id}.${item?.subId}.${item?.plantId}`,
          label: `${item?.subId} - ${item?.name}`,
          key: item?.id,
        })),
        totalItems: saleTeamMutate.totalItems,
      });
    }, [user, saleTeamMutate]);

    const handlePlantChange = (value: any) => {
      form.resetFields();
      form.setFieldsValue({
        ...defaultFilter,
        plantId: value,
      });

      // saleTeamMutate.mutate({
      //   plantId: value === '' ? undefined : value,
      //   pageSize: 10,
      // });
      // getUserRoleSaleInCBB({
      //   plantId: value === '' ? undefined : value,
      //   page: 1,
      //   pageSize: 10,
      // });
    };

    const handleTeamSelectScrollChange = useCallback(
      (params: ILazySelectParams) => {
        const { plantId } = form.getFieldsValue();
        const { pageSize, value } = params;

        saleTeamMutate.mutate({
          plantId: plantId,
          pageSize: pageSize,
          value,
          ...(defaultFilter?.plantId && { plantId: defaultFilter.plantId }),
          ...(user && user?.isQLD && { managerId: user.id }),
          ...(user && user?.isSale && { userId: user.id }),
        });
      },
      [],
    );

    const handleUserSelectScrollChange = (params: ILazySelectParams) => {
      const { plantId } = form.getFieldsValue();
      const { pageSize, value } = params;

      if (user?.isSale) {
        setUserData({
          ...userData,
          options: [{ label: user.email, value: user.id }],
        });
        return;
      }

      getUserRoleSaleInCBB({
        plantId: plantId,
        page: 1,
        pageSize: pageSize,
        email: value,
        ...(defaultFilter?.plantId && { plantId: defaultFilter.plantId }),
      });
    };

    return (
      <Content isOpen={isOpen}>
        <FormFilter form={form} onFinish={onFinish} onCancel={handleCancel}>
          <Row gutter={{ xs: 8, sm: 14 }}>
            <Col span={spanCol} className="h-[80px]">
              <Form.Item label="Chi nhánh" name="plantId">
                <SelectForm
                  placeholder="Chọn chi nhánh"
                  options={OPTIONS_PLANT}
                  onChange={handlePlantChange}
                  disabled={!(user?.isAdmin || user?.isHO)}
                  allowClear={false}
                  filterSort={() => 1}
                />
              </Form.Item>
            </Col>
            <Col span={spanCol} className="h-[80px]">
              <Form.Item label="Đội bán hàng" name="teamSaleId">
                <LazySelectForm
                  data={teamData}
                  loading={saleTeamMutate.isPending}
                  placeholder="Chọn đội"
                  onSelectScrollChange={handleTeamSelectScrollChange}
                />
              </Form.Item>
            </Col>
            <Col span={spanCol} className="h-[80px]">
              <Form.Item label="Thành viên" name="userId">
                <LazySelectForm
                  data={userData}
                  loading={isGetUserRoleSaleInCBBPending}
                  placeholder="Chọn thành viên"
                  onSelectScrollChange={handleUserSelectScrollChange}
                  disabled={user?.isSale}
                />
              </Form.Item>
            </Col>
            <Col span={spanCol} className="h-[80px]">
              <Form.Item label="Loại đội" name="teamTypeId">
                <SelectForm
                  placeholder="Chọn đội"
                  options={TEAM_TYPE_OPTIONS}
                />
              </Form.Item>
            </Col>
            <Col span={spanCol} className="h-[80px]">
              <Form.Item label="Trạng thái" name="statusId">
                <SelectForm
                  placeholder="Chọn trạng thái"
                  options={STATUS_TS_OPTIONS}
                />
              </Form.Item>
            </Col>
            {/* <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Khánh hàng" name="customerId">
              <LazySelectForm
                data={customerData}
                loading={isGetCustomerInComboBoxPending}
                placeholder="Chọn khách hàng"
                onSelectScrollChange={handleCustSelectScrollChange}
              />
            </Form.Item>
          </Col> */}
          </Row>
        </FormFilter>
      </Content>
    );
  },
);

export default FilterSaleTeamPage;
